import { TmdxResourceCard } from '@features/components/ResourceCards';
import { useDisclosure } from '@ds';
import { MdOutlineWifiTethering } from 'react-icons/md';
import { WithDetachResourceModal } from '@features/components/CaseOverview/components/AssignedResources/WithDetachResourceModal';
import { getUserMenuItems } from '@features/components/CaseOverview/components/AssignedResources/userMenu.config';
import { useDetachAssignedUser } from '@features/components/CaseOverview/components/AssignedResources/hooks/useDetachAssignedUser';
import { getFullName } from '@/utils/helpers';
import { externalIconGroups } from './config';
import { AssignedUser, AssignedUserData, CaseUserGroup, EmployeeLabels } from '@/api/apis/case';
import { Uuid } from '@/api/types';
import { EmployeeRole } from '@/api/apis/resources';
import { rolesCantBeDetached } from './AssignResourcesModal/config';
import { EmployeeModal } from './EmployeeModal';
import { roleByCaseUserGroup } from '@/constants/case/case.constants';
import { groupsWithMandatoryPrimary, primaryAvailableGroups, roleNames } from '@/constants/user.constants';
import { ConfirmRemovePrimaryModal } from './ConfirmRemovePrimaryModal';
import { useSetIsPrimaryUser } from '../hooks/useSetIsPrimaryUser';
import { SetIsPrimaryModal } from './SetIsPrimaryModal';
import { useUserSettingsStore } from '@/store/user-settings.store';
import { employeeLabelColors } from '@/constants/resources.constants';

type EmployeeCardProps = {
  employeeId: Uuid;
  user: AssignedUserData;
  group: CaseUserGroup;
  primaryAvailableUsers: Partial<Record<CaseUserGroup, AssignedUser[]>>;
  menuDisabled: boolean;
  caseId: Uuid;
  labels?: EmployeeLabels[];
  assignedUsersInGroup: AssignedUser[];
};

export const EmployeeCard = ({
  menuDisabled,
  employeeId,
  user,
  caseId,
  group,
  primaryAvailableUsers,
  labels,
  assignedUsersInGroup = [],
}: EmployeeCardProps) => {
  const {
    isOpen: isRemovePrimaryModalOpen,
    onOpenChange: onRemovePrimaryModalOpen,
    onClose: onRemovePrimaryModalClose,
  } = useDisclosure();
  const {
    isOpen: isSetPrimaryModalOpen,
    onOpenChange: onSetPrimaryModalOpen,
    onClose: onSetPrimaryModalClose,
  } = useDisclosure();
  const blockIsPrimaryModal =
    useUserSettingsStore((state) => state.blockPrimaryModalCaseIds).includes(caseId) ||
    Object.keys(primaryAvailableUsers).length === 0;
  const { handleDetach, isPendingDetach } = useDetachAssignedUser({ caseId });
  const { handleSetIsPrimaryUser, isPendingSetIsPrimaryUser } = useSetIsPrimaryUser({ caseId });
  const isPrimary = labels?.includes(EmployeeLabels.Primary);
  const fullName = getFullName(user.firstName, user.lastName);
  const roleOnCase = roleByCaseUserGroup[group] as EmployeeRole;
  const groupHasPrimary = group && primaryAvailableGroups.includes(group);
  const mandatoryPrimarySelected = isPrimary && groupsWithMandatoryPrimary.includes(group);
  const isPrimaryChangeAvailable = groupHasPrimary && !mandatoryPrimarySelected;
  const onSetIsPrimaryUser = () =>
    handleSetIsPrimaryUser({
      assignedUsers: primaryAvailableUsers,
      usersToUpdate: { [group]: [{ employeeId, isPrimary: !user.isPrimary }] },
    });
  const onRemoveIsPrimaryUser = () => handleSetIsPrimaryUser({ employeeId, group, onClose: onRemovePrimaryModalClose });
  const cantDetachRole = rolesCantBeDetached.includes(roleOnCase);
  const isLastOnCaseMandatory = groupsWithMandatoryPrimary.includes(group) && assignedUsersInGroup.length <= 1;

  const disabledKeys: string[] = [];
  if (cantDetachRole || isLastOnCaseMandatory) {
    disabledKeys.push('detach');
  }
  if (isLastOnCaseMandatory) {
    disabledKeys.push('primary');
  }

  return (
    <div className="w-80">
      <WithDetachResourceModal
        isPending={isPendingDetach}
        onDetachSubmit={() => handleDetach({ employeeId, group })}
        photo={user.photoPath}
        name={fullName}
      >
        {({ onOpenChange }) => (
          <EmployeeModal employeeId={employeeId}>
            <TmdxResourceCard
              icon={isPrimary && externalIconGroups.includes(group) ? <MdOutlineWifiTethering /> : null}
              menuDisabled={menuDisabled}
              menuProps={{ disabledKeys }}
              showChip={false}
              list={getUserMenuItems({
                onDetachPress: onOpenChange,
                onRemovePrimaryPress: onRemovePrimaryModalOpen,
                onSetAsPrimaryPress: onSetPrimaryModalOpen,
                handleSetIsPrimaryUser: onSetIsPrimaryUser,
                isPrimaryChangeAvailable,
                isPrimary,
                blockIsPrimaryModal,
                caseId,
                isLastOnCaseMandatory,
              })}
              type="user"
              title={fullName}
              subTitle={roleOnCase ? roleNames[roleOnCase] : ''}
              phoneNumber={user.phone}
              avatarProps={{
                src: user.photoPath,
                label: labels?.[0] || undefined,
                isStatus: true,
                chipProps: {
                  color: labels?.length ? employeeLabelColors[labels[0]] : undefined,
                },
              }}
            />
          </EmployeeModal>
        )}
      </WithDetachResourceModal>
      {isRemovePrimaryModalOpen && (
        <ConfirmRemovePrimaryModal
          isPending={isPendingSetIsPrimaryUser}
          isOpen={isRemovePrimaryModalOpen}
          onOpenChange={onRemovePrimaryModalOpen}
          name={fullName}
          onSubmit={onRemoveIsPrimaryUser}
        />
      )}
      {isSetPrimaryModalOpen && (
        <SetIsPrimaryModal
          isPending={isPendingSetIsPrimaryUser}
          isOpen={isSetPrimaryModalOpen}
          onOpenChange={onSetPrimaryModalOpen}
          onSubmit={handleSetIsPrimaryUser}
          primaryAvailableUsers={primaryAvailableUsers}
          caseId={caseId}
          onClose={onSetPrimaryModalClose}
          selectedEmployeeId={employeeId}
          selectedGroup={group}
        />
      )}
    </div>
  );
};
